/**
 * GitHub Link Banner component.
 * Displayed when an Entra ID user doesn't have an authorized GitHub Copilot connector.
 * Prompts them to link their GitHub account for a complete experience.
 *
 * @module components/layout/GitHubLinkBanner
 */

import { useState, useEffect, useCallback } from 'react';
import { Github, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { connectionService } from '@/services/connectionService';

/** Height of the GitHub link banner in pixels */
export const GITHUB_LINK_BANNER_HEIGHT = 44;

interface GitHubLinkBannerProps {
  onVisibilityChange?: (visible: boolean) => void;
}

/**
 * Banner displayed at the top of the page when an Entra ID user
 * doesn't have a ready GitHub Copilot connection.
 */
export function GitHubLinkBanner({ onVisibilityChange }: GitHubLinkBannerProps) {
  const { user } = useAuth();
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [linking, setLinking] = useState(false);
  const [needsReauth, setNeedsReauth] = useState(false);

  const updateVisibility = useCallback(
    (visible: boolean) => {
      setShowBanner(visible);
      onVisibilityChange?.(visible);
    },
    [onVisibilityChange]
  );

  // Check copilot status for Entra ID users
  useEffect(() => {
    if (user?.authType !== 'entra_id' || dismissed) {
      updateVisibility(false);
      return;
    }

    // Check if user previously dismissed (per session)
    const wasDismissed = sessionStorage.getItem('github_link_banner_dismissed');
    if (wasDismissed === 'true') {
      setDismissed(true);
      updateVisibility(false);
      return;
    }

    // Check if just linked (redirect with ?github_linked=true)
    const params = new URLSearchParams(window.location.search);
    if (params.get('github_linked') === 'true') {
      updateVisibility(false);
      // Clean up URL
      params.delete('github_linked');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      return;
    }

    let cancelled = false;
    connectionService
      .getCopilotStatus()
      .then((status) => {
        if (!cancelled) {
          if (status.needsReauthorization) {
            setNeedsReauth(true);
            updateVisibility(true);
          } else if (!status.hasReadyCopilotConnection) {
            updateVisibility(true);
          }
        }
      })
      .catch(() => {
        // Silently ignore errors — don't show banner if we can't check
      });

    return () => {
      cancelled = true;
    };
  }, [user, dismissed, updateVisibility]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    updateVisibility(false);
    sessionStorage.setItem('github_link_banner_dismissed', 'true');
  }, [updateVisibility]);

  const handleLinkGitHub = useCallback(async () => {
    setLinking(true);
    try {
      const result = await connectionService.linkGitHub(window.location.href);
      window.location.href = result.consentLink;
    } catch (error) {
      console.error('Failed to generate GitHub link URL:', error);
      setLinking(false);
    }
  }, []);

  if (!showBanner) {
    return null;
  }

  return (
    <div className="bg-linear-to-r from-amber-500 to-yellow-400 text-gray-900 px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-3 shadow-md">
      <Github className="h-4 w-4 shrink-0" />
      <span>
        {needsReauth ? (
          <>
            Re-authorize your <strong>GitHub account</strong> to keep Copilot tokens refreshing
            automatically.
          </>
        ) : (
          <>
            Link your <strong>GitHub account</strong> for GitHub Copilot integration.
          </>
        )}
      </span>
      <button
        type="button"
        onClick={handleLinkGitHub}
        disabled={linking}
        className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-900 text-white rounded-md text-xs font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50"
      >
        <Github className="h-3.5 w-3.5" />
        {linking ? 'Redirecting...' : needsReauth ? 'Re-authorize GitHub' : 'Authorize GitHub'}
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        className="ml-2 p-0.5 rounded-sm hover:bg-amber-600/20 transition-colors"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
